import React, { useState, useEffect, useCallback } from 'react';
import { defaultAxiosInstanceReward } from '../api/defaultInstance';
import Layout from './Layout';
import '../css/ConsumptionVolumeCheck.css';

function ConsumptionVolumeCheck() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [distributor, setDistributor] = useState('avatye');
  const [clickedRowIndex, setClickedRowIndex] = useState(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  
  // 월 선택 상태 추가
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  // 월 포맷팅 함수 (yyyy.MM)
  const formatMonth = (year, month) => {
    return `${year}.${String(month).padStart(2, '0')}`;
  };

  // 이전 월로 이동
  const handlePrevMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 1) {
        return { year: prev.year - 1, month: 12 };
      } else {
        return { year: prev.year, month: prev.month - 1 };
      }
    });
  };

  // 다음 월로 이동
  const handleNextMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 12) {
        return { year: prev.year + 1, month: 1 };
      } else {
        return { year: prev.year, month: prev.month + 1 };
      }
    });
  };

  const fetchData = useCallback(async () => {
    if (!distributor.trim()) {
      setError('개발사를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // API 호출 시 월 정보를 쿼리 파라미터로 전달
      const response = await defaultAxiosInstanceReward.get(
        `/reward-admin/campaign/accumulated-count/${encodeURIComponent(distributor)}`,
        {
          params: {
            year: selectedMonth.year,
            month: selectedMonth.month
          }
        }
      );
      
      if (response.data.status === 200) {
        let filteredData = response.data.data;
        
        // 조회하는 월이 오늘을 포함한 달이라면 오늘날짜 까지만 출력 (KST 기준)
        const now = new Date();
        // KST 기준으로 현재 날짜 정보 추출
        const kstFormatter = new Intl.DateTimeFormat("ko-KR", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          timeZone: "Asia/Seoul"
        });
        const kstParts = kstFormatter.formatToParts(now);
        const kstYear = parseInt(kstParts.find(p => p.type === 'year').value);
        const kstMonth = parseInt(kstParts.find(p => p.type === 'month').value);
        const kstDay = parseInt(kstParts.find(p => p.type === 'day').value);
        
        const isCurrentMonth = 
          selectedMonth.year === kstYear && 
          selectedMonth.month === kstMonth;
        
        if (isCurrentMonth) {
          // KST 기준 오늘 날짜 생성
          const today = new Date(kstYear, kstMonth - 1, kstDay);
          today.setHours(0, 0, 0, 0);
          
          filteredData = response.data.data.filter(item => {
            const itemDate = new Date(item.date);
            itemDate.setHours(0, 0, 0, 0);
            return itemDate <= today;
          });
        }
        
        setData(filteredData);
      } else {
        setError(response.data.message || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('API 호출 오류:', err);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [distributor, selectedMonth]);

  // 개발사 또는 월이 변경될 때마다 데이터 조회
  useEffect(() => {
    if (distributor) {
      fetchData();
    }
  }, [distributor, selectedMonth, fetchData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const dayOfWeek = date.toLocaleDateString('ko-KR', {
      weekday: 'short'
    });
    const dayOfWeekNumber = date.getDay(); // 0: 일요일, 6: 토요일
    
    let dayColor = '';
    if (dayOfWeekNumber === 0) { // 일요일
      dayColor = 'color: red;';
    } else if (dayOfWeekNumber === 6) { // 토요일
      dayColor = 'color: blue;';
    }
    
    return (
      <span>
        {dateStr} (<span style={{ color: dayOfWeekNumber === 0 ? 'red' : dayOfWeekNumber === 6 ? 'blue' : 'inherit' }}>{dayOfWeek}</span>)
      </span>
    );
  };

  const getTotalCount = () => {
    return data.reduce((sum, item) => sum + item.consumedCount, 0);
  };

  // 타입명을 한글 표시명으로 변환
  const getTypeDisplayName = (typeKey) => {
    const typeMapping = {
      'findRoad': '도보',
      'findParkingLot': '주차장',
      'save': '저장',
      'findReviewCount': '리뷰수',
      'smartCall': '스마트콜',
      'findSight': '명소'
    };

    // findRoad_NPM0 같은 형태 처리 (첫 번째 _ 기준으로 분리)
    const underscoreIndex = typeKey.indexOf('_');
    if (underscoreIndex !== -1) {
      const baseType = typeKey.substring(0, underscoreIndex);
      const suffix = typeKey.substring(underscoreIndex + 1);
      const baseDisplayName = typeMapping[baseType] || baseType;
      return `${baseDisplayName}_${suffix}`;
    }

    return typeMapping[typeKey] || typeKey;
  };

  // 행 클릭 핸들러
  const handleRowClick = (index) => {
    if (clickedRowIndex === index) {
      // 같은 행을 다시 클릭하면 툴팁 닫기
      setClickedRowIndex(null);
    } else {
      // 다른 행 클릭 시 해당 행의 툴팁 표시
      setClickedRowIndex(index);
    }
  };

  // 행 호버 핸들러
  const handleRowMouseEnter = (index) => {
    setHoveredRowIndex(index);
  };

  const handleRowMouseLeave = () => {
    setHoveredRowIndex(null);
  };

  // 툴팁 닫기 (외부 클릭 시)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.table-row') && !event.target.closest('.quota-tooltip')) {
        setClickedRowIndex(null);
      }
    };

    if (clickedRowIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [clickedRowIndex]);

  return (
    <Layout>
      <div className="consumption-volume-check">
        <div className="page-header">
          <h1>소진물량 확인</h1>
          <p>개발사별 월별 소진물량을 확인할 수 있습니다.</p>
        </div>

        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="form-group">
              <label htmlFor="distributor">개발사</label>
              <select
                id="distributor"
                value={distributor}
                onChange={(e) => setDistributor(e.target.value)}
                className="form-control"
                required
              >
                <option value="avatye">아바티</option>
                <option value="buzzvil">버즈빌</option>
                <option value="konnet">코넷</option>
              </select>
            </div>
            <div className="form-group">
              <label>조회 월</label>
              <div className="month-selector">
                <button
                  type="button"
                  className="month-nav-btn"
                  onClick={handlePrevMonth}
                  disabled={loading}
                  aria-label="이전 월"
                >
                  ←
                </button>
                <span className="month-display">
                  {formatMonth(selectedMonth.year, selectedMonth.month)}
                </span>
                <button
                  type="button"
                  className="month-nav-btn"
                  onClick={handleNextMonth}
                  disabled={loading}
                  aria-label="다음 월"
                >
                  →
                </button>
              </div>
            </div>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? '조회 중...' : '조회'}
            </button>
          </form>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {data.length > 0 && (
          <div className="results-section" style={{ maxHeight: 'none', height: 'auto', overflow: 'visible' }}>
            <div className="table-container" style={{ maxHeight: 'none', height: 'auto', overflow: 'visible' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>접수 물량</th>
                    <th>세팅 물량</th>
                    <th>소진 물량(총합: {getTotalCount().toLocaleString()})</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const isClicked = clickedRowIndex === index;
                    const isHovered = hoveredRowIndex === index;
                    const showTooltip = isClicked || isHovered;
                    const quotaByType = item.quotaByType || {};
                    const hasQuotaByType = Object.keys(quotaByType).length > 0;

                    return (
                      <tr 
                        key={index} 
                        className={`table-row ${isClicked ? 'clicked' : ''}`}
                        onClick={() => handleRowClick(index)}
                        onMouseEnter={() => handleRowMouseEnter(index)}
                        onMouseLeave={handleRowMouseLeave}
                        style={{ cursor: hasQuotaByType ? 'pointer' : 'default' }}
                      >
                        <td>{formatDate(item.date)}</td>
                        <td className="count-cell">{item.receivedCount.toLocaleString()}</td>
                        <td className="count-cell quota-cell" style={{ position: 'relative' }}>
                          <span>{item.quotaCount.toLocaleString()}</span>
                          {showTooltip && hasQuotaByType && (
                            <div className="quota-tooltip">
                              <div className="tooltip-header">
                                {new Date(item.date).toLocaleDateString('ko-KR', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  weekday: 'short'
                                })}
                              </div>
                              <div className="tooltip-content">
                                {Object.entries(quotaByType).map(([type, quota]) => (
                                  <div key={type} className="tooltip-item">
                                    <span className="tooltip-type">{getTypeDisplayName(type)}</span>
                                    <span className="tooltip-quota">{quota.toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="count-cell">{item.consumedCount.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ConsumptionVolumeCheck;